import { tenantTz } from "./company";

// Wall-clock strings ("YYYY-MM-DDTHH:mm") are always in the TENANT's
// timezone, so a dispatcher on a device set to a different zone still
// books/reads times in company-local time.

const pad = (n) => String(n).padStart(2, "0");

function partsIn(date, tz) {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const o = {};
  for (const p of f.formatToParts(date)) o[p.type] = p.value;
  return o;
}

function offsetMs(ts, tz) {
  const p = partsIn(new Date(ts), tz);
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUtc - Math.floor(ts / 1000) * 1000;
}

export function toTenantWall(date = new Date()) {
  const p = partsIn(date, tenantTz());
  return `${p.year}-${p.month}-${p.day}T${pad(+p.hour % 24)}:${p.minute}`;
}

export function wallToDate(wall) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(wall || "");
  if (!m) return new Date(NaN);
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
  const tz = tenantTz();
  let ts = guess - offsetMs(guess, tz);
  ts = guess - offsetMs(ts, tz);
  return new Date(ts);
}

export const wallToIso = (wall) => wallToDate(wall).toISOString();

export const tenantTodayIso = () => toTenantWall().slice(0, 10);

// Shift a "YYYY-MM-DD" by whole days (calendar math, DST-proof).
export function addDaysIso(ymd, days) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

// Local midnight `daysAgo` days back, in the tenant's timezone.
export const tenantDayStart = (daysAgo = 0) => wallToDate(addDaysIso(tenantTodayIso(), -daysAgo));

// 0 = Sunday … 6 = Saturday, in the tenant's timezone.
export function tenantWeekday(dateOrIso) {
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: tenantTz(), weekday: "short" })
    .format(new Date(dateOrIso));
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(wd);
}

// Calendar widgets work in device-local Date objects: convert both ways.
export function wallToCalendarDate(wall) {
  if (!wall) return undefined;
  const [y, m, d] = wall.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

export const calendarDateToYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Whole calendar days a vehicle is past its impound deadline, counted in the
// tenant's timezone. `impound_release_due_at` IS the local midnight that
// starts the first overdue day, so the count is how many local dates have
// begun since then: collected on the final day = 0, next day = 1, and so on.
//
// Oct 2026 — four screens each ran `Math.ceil(ms / 86400000)` off the browser
// clock, which made the overstay day start at whatever time of day the
// vehicle was seized and disagreed with the yard's own midnight rollover.
export function overstayDays(dueIso, now = new Date()) {
  if (!dueIso) return 0;
  const due = new Date(dueIso);
  if (isNaN(due.getTime()) || now.getTime() <= due.getTime()) return 0;
  const a = toTenantWall(due).slice(0, 10);
  const b = toTenantWall(now).slice(0, 10);
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const diff = Math.round(
    (Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000
  );
  return Math.max(diff + 1, 1);
}

// Whole calendar days until the deadline, tenant-local. 0 = the final free
// day ("Due today"), 1 = due tomorrow.
export function daysUntilDue(dueIso, now = new Date()) {
  if (!dueIso) return null;
  const due = new Date(dueIso);
  if (isNaN(due.getTime())) return null;
  const a = toTenantWall(now).slice(0, 10);
  const b = toTenantWall(due).slice(0, 10);
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round(
    (Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000
  );
}

// The last day an impound can be collected without an overstay charge.
// `impound_release_due_at` is the local midnight that STARTS the first
// overdue day, so showing it raw told staff and owners they had until the
// 25th when the deadline was the end of the 24th. Returns an ISO instant at
// midday local on that last free day, so existing date formatters are safe.
export function lastFreeDayIso(dueIso) {
  if (!dueIso) return null;
  const due = new Date(dueIso);
  if (isNaN(due.getTime())) return null;
  // Calendar arithmetic on the wall date — subtracting 24h would land on the
  // wrong date when the clocks change.
  const ymd = addDaysIso(toTenantWall(due).slice(0, 10), -1);
  return wallToDate(`${ymd}T12:00`).toISOString();
}
